import axios, {
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { refreshAccessToken, ensureAccessToken } from "@/lib/auth-cookies";
import { setAccessTokenCookie } from "@/lib/auth-cookies";
import { getUTMParams } from "@/lib/utm-utils";
import { getAnonymousIdCookie } from "@/lib/session-utils";
import {
  isAxiosDebugEnabled,
  logAxiosRequest,
  logAxiosResponse,
  logAxiosError,
} from "@/api/clients/axios-logger";

const addUTMParams = async (config: InternalAxiosRequestConfig<unknown>) => {
  // const utmParams = await getUTMParams();

  // if (Object.keys(utmParams).length > 0) {
  //   const url = new URL(config.url!, config.baseURL);

  //   Object.entries(utmParams).forEach(([key, value]) => {
  //     url.searchParams.set(key, value);
  //   });

  //   config.url = url.toString();
  //   config.baseURL = "";
  // }

  // return config;
  const utmParams = await getUTMParams();
  if (!utmParams || Object.keys(utmParams).length === 0) return config;

  config.headers = config.headers ?? {};
  const headers = config.headers as Record<string, string>;

  Object.entries(utmParams).forEach(([key, value]) => {
    const normalized = key.replace(/_/g, "-").toLowerCase();
    const headerName = `${normalized}`;
    headers[headerName] = String(value);
  });

  return config;
};

const addAnonymousId = async (config: InternalAxiosRequestConfig<unknown>) => {
  const anonymousId = await getAnonymousIdCookie();
  if (anonymousId) {
    config.headers["Anonymous-Id"] = anonymousId;
  }
  return config;
};

const addAuthToken = async (config: InternalAxiosRequestConfig<unknown>) => {
  const accessToken = await ensureAccessToken();
  if (accessToken) {
    config.headers.Authorization = `${accessToken}`;
  }
  return config;
};

const handleAuthError = async (error: AxiosError) => {
  const originalRequest = error.config as AxiosRequestConfig & {
    _retry?: boolean;
  };

  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    try {
      const result = await refreshAccessToken();
      if (result.success && result.access_token) {
        await setAccessTokenCookie(result.access_token);
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `${result.access_token}`,
        };
        return authenticatedLykkeClient(originalRequest);
      }
    } catch (err) {
      console.error("Token refresh failed:", err);
    }
  }

  return Promise.reject(error);
};

const createClient = (withAuth = false) => {
  const client = axios.create({
    baseURL: process.env.NEXT_PUBLIC_LYKKE_DEV,
    headers: { "Content-Type": "application/json" },
    timeout: 90000, // 90 seconds timeout
  });

  client.interceptors.request.use(async (config) => await addUTMParams(config));
  client.interceptors.request.use(
    async (config) => await addAnonymousId(config)
  );

  if (withAuth) {
    client.interceptors.request.use(addAuthToken);
    client.interceptors.response.use((response) => response, handleAuthError);
  }

  if (isAxiosDebugEnabled) {
    client.interceptors.request.use(logAxiosRequest);
    client.interceptors.response.use(logAxiosResponse, logAxiosError);
  }

  return client;
};

const lykkeClient = createClient();
const authenticatedLykkeClient = createClient(true);

export default lykkeClient;
export { authenticatedLykkeClient };
